import {
  KeyboardEventHandler,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import {
  type UseSelectState,
  type UseSelectStateChangeOptions,
  useSelect,
  GetToggleButtonPropsOptions,
} from 'downshift';

import { mergeRefs, useForwardedRef } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import useDropdownId from './useDropdownId';
import useGroupedItems from './useGroupedItems';
import useKeepMenuOpenOnBlur from './useKeepMenuOpenOnBlur';
import useVirtualProps from './useVirtualProps';
import {
  checkReferenceEquality,
  defaultLabelGetter,
  getAriaProps,
  isGroupHeader,
  wrapGetItemProps,
} from '../helpers';
import { getSingleSelectionHandlers } from '../helpers/sharedSingleSelection';
import type {
  DefaultItem,
  DropdownGetMenuProps,
  GenericItem,
  GroupHeader,
  InputItemGroup,
  UseDropdownProps,
  GetItemProps,
  GetToggleButtonProps,
} from '../types';
import { useGroupFilter } from './index';
import useCollapsibleGroups from './useCollapsibleGroups';

/**
 * A hook used to manage all logic needed for a Dropdown.
 */
const useDropdown = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement,
>({
  id,
  itemToString = (item) => defaultLabelGetter(item as DefaultItem | null),
  items,
  groupedItems,
  target,
  overlayRef: overlayRefProp,
  layoutDependency,
  getMenuProps: getMenuPropsProp,
  stateReducer: stateReducerProp,
  ...props
}: UseDropdownProps<TItem, TTarget>) => {
  const dropdownId = useDropdownId(id);
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality({ ...props, items: maybeGroupedItems });

  const ariaProps = getAriaProps(props);
  const {
    allowCollapsibleGroups,
    showGroupItemCount,
    allowGroupFiltering,
    showEmptyGroups,
  } = props;

  const targetRef = useForwardedRef(target || null);
  const overlayRef = useForwardedRef(overlayRefProp || null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string>('group0');
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>();

  const {
    flatItems: flatGroupedItems,
    indexGetter,
    getMenuItems,
  } = useGroupedItems({
    maybeGroupedItems,
    allowCollapsibleGroups,
    expandedGroupKey,
    selectedGroupKey,
  });

  const { filteredGroupItems: flatItems, showGroupHeaders } = useGroupFilter({
    items: flatGroupedItems,
    maybeGroupedItems,
    selectedGroupKey,
  });

  const { onKeyDown: controlOnKeyDown, getShouldKeepMenuOpenOnBlur } =
    useKeepMenuOpenOnBlur({ id: dropdownId });

  const dropdownStateReducer = useCallback(
    (
      state: UseSelectState<TItem>,
      { changes, type }: UseSelectStateChangeOptions<TItem>,
    ): Partial<UseSelectState<TItem>> => {
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonBlur:
          return {
            ...changes,
            isOpen: getShouldKeepMenuOpenOnBlur(),
          };
        default:
          return changes;
      }
    },
    [getShouldKeepMenuOpenOnBlur],
  );

  const stateReducer = useCallback(
    (
      state: UseSelectState<TItem>,
      actionAndChanges: UseSelectStateChangeOptions<TItem>,
    ) => {
      const updatedState = dropdownStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [dropdownStateReducer, stateReducerProp],
  );

  const {
    isOpen: downshiftIsMenuOpen,
    getItemProps: rawGetItemProps,
    selectItem,
    getMenuProps: rawGetMenuProps,
    getToggleButtonProps: rawGetToggleButtonProps,
    openMenu,
    highlightedIndex,
    selectedItem,
    ...useSelectReturnValue
  } = useSelect({
    id: dropdownId,
    itemToString,
    items: flatItems,
    stateReducer,
    ...props,
  });

  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps,
  } = useOverlay({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    onHide: useSelectReturnValue.closeMenu,
    target: targetRef,
    overlayRef,
    layoutDependency,
    isOverlayConditionallyRendered: false,
    ...props,
  });

  const getMenuProps: DropdownGetMenuProps = useCallback(
    (options, otherOptions) => {
      const temp = getMenuPropsProp
        ? rawGetMenuProps(
            getMenuPropsProp({ ...overlayProps, ...options }, otherOptions),
            otherOptions,
          )
        : rawGetMenuProps({ ...overlayProps, ...options }, otherOptions);

      return temp;
    },
    [getMenuPropsProp, overlayProps, rawGetMenuProps],
  ) as DropdownGetMenuProps;

  // TODO: deduplicate this
  type MenuItems = typeof groupedItems extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];

  const menuItems = useMemo(() => {
    // This is a required extra step to get proper menu item counts from
    // filteredItems which are all filtered items but not group filtered.
    // The group filter requires these counts to ensure group filter buttons
    // behave correctly
    getMenuItems(flatGroupedItems);
    return getMenuItems(flatItems, false) as MenuItems;
  }, [flatItems, flatGroupedItems, getMenuItems]);

  const defaultIndexGetter = useCallback(
    (item: TItem) => menuItems.indexOf(item),
    [menuItems],
  );

  const getItemProps: GetItemProps<TItem> = useCallback(
    (args) =>
      wrapGetItemProps({
        getItemProps: rawGetItemProps,
        highlightedIndex,
        selectedItem,
        indexGetter: indexGetter || defaultIndexGetter,
      })(args),
    [
      defaultIndexGetter,
      highlightedIndex,
      indexGetter,
      rawGetItemProps,
      selectedItem,
    ],
  );

  const { setExpandedGroup } = useCollapsibleGroups({
    allowCollapsibleGroups,
    flatItems,
    maybeGroupedItems,
    highlightedIndex,
    menuItems,
    expandedGroupKey,
    setExpandedGroupKey,
  });

  const getToggleButtonProps = useCallback(
    ({
      onKeyDown: onKeyDownProp,
      ...options
    }: GetToggleButtonPropsOptions = {}) => {
      const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
        controlOnKeyDown(e);

        onKeyDownProp?.(e);
      };

      return rawGetToggleButtonProps({ ...options, onKeyDown });
    },
    [controlOnKeyDown, rawGetToggleButtonProps],
  ) as GetToggleButtonProps;

  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);

  const virtualProps = useVirtualProps({
    menuItems,
    isGroupHeader,
    showEmptyGroups,
    showGroupHeaders,
  });

  const singleSelectionHandlers = getSingleSelectionHandlers({
    id: dropdownId,
    selectItem,
    closeMenu: useSelectReturnValue.closeMenu,
    refToFocusOnClear: targetRef,
    selectedItem,
  });

  return {
    controlProps: {
      ref: targetRef,
      isOpen: useOverlayIsOpen,
      isAutocomplete: false,
      getToggleButtonProps,
      openMenu,
      selectedItem,
      itemToString,
      selectItem: selectItem as (item: TItem | null) => void,
      ...(props['data-testid'] ? { 'data-testid': props['data-testid'] } : {}),
      ...ariaProps,
    },
    menuProps: {
      isOpen: useOverlayIsOpen,
      isAutocomplete: false,
      menuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex,
      selectedItem,
      width: targetRef.current?.offsetWidth,
      selectItem,
      isGroupHeader,
      allowCollapsibleGroups,
      allowGroupFiltering,
      showGroupItemCount,
      groupedItems: maybeGroupedItems,
      setExpandedGroupKey: setExpandedGroup,
      expandedGroupKey,
      selectedGroupKey,
      setSelectedGroupKey,
      showGroupHeaders,
      setHighlightedIndex: useSelectReturnValue.setHighlightedIndex,
      refToFocusOnClick: targetRef,
      showEmptyGroups,
      dropdownId,
      ...singleSelectionHandlers.menuProps,
    },
    /**
     * Props to be provided to `@tanstack/react-virtual`'s `useVirtual` hook when using virtualization.
     */
    virtualProps,
    selectItem,
    ...useSelectReturnValue,
  };
};

export default Object.assign(useDropdown, {
  stateChangeTypes: useSelect.stateChangeTypes,
});
